import { useSetAtom } from 'jotai';
import { useCallback } from 'react';

import { logger } from '../../datadog';
import { useAtomCallbackMemoized } from '../../hooks/useAtomCallbackMemoized';
import { UserType } from '../../types';
import { DataChannelMessageType, DataChannelSessionIntentionSetMessage, useSendMessage } from '../../video-sdk';
import { participantUserIdsAtom } from '../../video-sdk/core/room/participant/atoms';
import { inSessionPatientInformationAtom, providerShortIdAtom } from '../state';

export function useSendSessionIntentionSetCallback() {
  const getProviderShortId = useAtomCallbackMemoized((get) => get(providerShortIdAtom), []);
  const getParticipantUserIds = useAtomCallbackMemoized((get) => get(participantUserIdsAtom), []);
  const getInSessionPatientInfo = useAtomCallbackMemoized((get) => get(inSessionPatientInformationAtom), []);
  const sendMessage = useSendMessage();
  const setInSessionPatientInformation = useSetAtom(inSessionPatientInformationAtom);

  return useCallback(
    async (shortId: string) => {
      logger.info('Sent data channel message', {
        messageType: DataChannelMessageType.SESSION_INTENTION_SET,
        sender: 'provider',
      });

      const completedAt = new Date().toISOString();
      const message: DataChannelSessionIntentionSetMessage = {
        type: DataChannelMessageType.SESSION_INTENTION_SET,
        sender: {
          entityId: getProviderShortId() ?? '',
          entityType: UserType.PROVIDER,
        },
        sessionIntention: {
          shortId,
          completedAt,
        },
      };

      const didSendMessage = await sendMessage(message);

      if (didSendMessage) {
        const participantUserIds = getParticipantUserIds();
        const inSessionPatient = getInSessionPatientInfo();
        if (inSessionPatient && participantUserIds.includes(inSessionPatient.visitorUuid || '')) {
          setInSessionPatientInformation({
            ...inSessionPatient,
            intentionCompletedAt: completedAt,
          });
        }
      }

      return didSendMessage;
    },
    [sendMessage, getProviderShortId, getParticipantUserIds, getInSessionPatientInfo, setInSessionPatientInformation],
  );
}
